import React, { useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  Target,
  TrendingUp,
  Globe,
  Gauge,
  User,
} from "lucide-react";
import { Reveal } from "./ui/Reveal";

const usps = [
  { label: "Limited, high-intent engagements", icon: Target },
  { label: "CRO & revenue-aligned media", icon: TrendingUp },
  { label: "Global execution, local sensitivity", icon: Globe },
  { label: "Measured on performance, not activity", icon: Gauge },
];

const WhyChooseMe = () => {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden group bg-neutral-100 aspect-[4/3] lg:aspect-auto lg:h-full">
                {!imgFailed ? (
                  <img
                    src="/assets/abhinavdesktop.png"
                    alt="Abhinav Joshi working at his desk"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-neutral-400 p-8 text-center">
                    <span className="w-14 h-14 rounded-full bg-neutral-200 flex items-center justify-center">
                      <User className="w-6 h-6" />
                    </span>
                    <span className="text-sm">
                      Photo failed to load — check that{" "}
                      <code className="font-mono">abhinavdesktop.png</code> exists in{" "}
                      <code className="font-mono">public/assets/</code>
                    </span>
                  </div>
                )}
              </div>
              <div className="absolute top-5 right-5 bg-white rounded-xl shadow-lg border border-black/5 px-4 py-3 flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-neutral-950 leading-none">Personally managed</div>
                  <div className="text-[11px] text-neutral-500 mt-1 leading-none">No hand-offs to juniors</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-neutral-50">
              Feature
            </span>
            <h2 className="mt-5 text-[26px] sm:text-[36px] leading-[1.1] tracking-[-0.02em] font-semibold text-neutral-950">
              Why Businesses Choose to Work With Me, Not Agencies?
            </h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              This model works best for businesses that want a thinking partner, not just execution support.
            </p>

            <div className="mt-7 relative inline-flex group">
              <div className="absolute -inset-1 rounded-xl opacity-60 blur-lg bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 transition-opacity duration-300 group-hover:opacity-90" />
              <a
                href="/connect"
                className="relative z-10 inline-flex items-center gap-2 bg-black text-white pl-5 pr-4 py-3 rounded-xl text-[15px] font-medium transition-transform duration-200 group-hover:-translate-y-0.5"
              >
                Work with me directly
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-8 flex flex-col gap-2.5">
              {usps.map(({ label, icon: Icon }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 border border-black/10 rounded-full px-4 py-2 w-fit bg-white"
                >
                  <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-neutral-700" />
                  </span>
                  <span className="text-sm text-neutral-700">{label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseMe;
